import type { Person } from '@tive/core';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { cn, initials } from '../lib/utils';

/**
 * Somebody's face, or their initials.
 *
 * The fallback is not a nicety: no source of org data we read carries photos,
 * so most orgs will have none, and a broken image icon in a hundred cards is
 * worse than no photo at all. A URL that fails to load falls back too — the
 * file is hand-maintained, and links rot.
 */
export function Avatar({
  person,
  size,
  colors,
  className,
}: {
  person: Person;
  /** Pixels. The card uses 32, the details panel 44, a list row 24. */
  size: number;
  colors: { bg: string; fg: string };
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const photo = failed ? null : person.photoUrl;

  return (
    <span
      style={{
        width: size,
        height: size,
        ...(photo ? {} : { background: colors.bg, color: colors.fg }),
        fontSize: Math.round(size * 0.34),
      }}
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold',
        person.isNew && !photo && 'border border-dashed border-current',
        className,
      )}
    >
      {photo ? (
        <img
          src={photo}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : person.isNew ? (
        <Plus size={Math.round(size * 0.44)} />
      ) : (
        initials(person.name)
      )}
    </span>
  );
}
